import { getImageDimensions } from "./utils";

// In-memory cache for instant synchronous lookups (e.g., pre-cached uploads)
export const mediaCache = new Map();

// The timeline shows the 400x300 thumbnail (see loadMediaWithPersistentCache);
// the lightbox needs the original. Same mxc URL, different bytes, so originals
// get their own in-memory map and their own key in the persistent cache —
// sharing either would let a thumbnail answer for a full-size request.
const fullMediaCache = new Map();

// Persistent browser cache name – survives page reloads
const MEDIA_CACHE_NAME = "bryght-chat-media-v1";

// Mirrors tuwunel's max_request_size (infrastructure/files/tuwunel.toml). One
// number for every file type: two limits in two places drift apart. Without this
// the homeserver answers an oversized upload with a bare 413 that reaches the
// user as "konnte nicht hochgeladen werden" — true, but not actionable.
export const MAX_UPLOAD_BYTES = 24 * 1024 * 1024;

// Loads that are still on their way, so a second caller for the same file
// (the voice player asking while the timeline prefetches) waits for the one
// download instead of starting another.
const pendingMediaLoads = new Map();

export function loadAuthenticatedMedia(mxcUrl, el, mediaType = "image", mimeType = null) {
  if (!mxcUrl || !mxcUrl.startsWith("mxc://")) return;

  if (mediaCache.has(mxcUrl)) {
    el.src = mediaCache.get(mxcUrl);
    return;
  }

  resolveAuthenticatedMediaUrl(mxcUrl, mediaType, mimeType).then((blobUrl) => {
    if (blobUrl) el.src = blobUrl;
  });
}

// The blob URL of a Matrix file, or null when it could not be loaded. Never
// rejects.
export function resolveAuthenticatedMediaUrl(mxcUrl, mediaType = "image", mimeType = null) {
  if (!mxcUrl || !mxcUrl.startsWith("mxc://")) return Promise.resolve(null);
  if (mediaCache.has(mxcUrl)) return Promise.resolve(mediaCache.get(mxcUrl));
  if (pendingMediaLoads.has(mxcUrl)) return pendingMediaLoads.get(mxcUrl);

  const load = loadMediaWithPersistentCache(mxcUrl, mediaType, mimeType)
    .finally(() => pendingMediaLoads.delete(mxcUrl));
  pendingMediaLoads.set(mxcUrl, load);
  return load;
}

async function loadMediaWithPersistentCache(mxcUrl, mediaType, mimeType) {
  try {
    const parts = mxcUrl.replace("mxc://", "").split("/");
    if (parts.length < 2) return null;

    const serverName = parts[0];
    const mediaId = parts.slice(1).join("/");

    // Optional on purpose: without Cache Storage (private modes, insecure
    // contexts) the file still loads, it just isn't kept between reloads.
    const cache = typeof caches === "undefined" ? null : await caches.open(MEDIA_CACHE_NAME).catch(() => null);
    const cacheKey = mxcUrl.replace("mxc://", "https://bryght-media-cache/");

    const cachedResponse = await cache?.match(cacheKey).catch(() => null);
    if (cachedResponse) {
      const blob = await typedMediaBlob(await cachedResponse.blob(), mediaType, mimeType);
      const blobUrl = URL.createObjectURL(blob);
      mediaCache.set(mxcUrl, blobUrl);
      return blobUrl;
    }

    const container = document.getElementById("bryght-chat");
    const { homeserverUrl, accessToken } = container.dataset;

    let url;
    if (mediaType === "image" && mimeType !== "image/gif") {
      url = `${homeserverUrl}/_matrix/client/v1/media/thumbnail/${serverName}/${mediaId}?width=400&height=300&method=scale`;
    } else {
      url = `${homeserverUrl}/_matrix/client/v1/media/download/${serverName}/${mediaId}`;
    }

    let res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!res.ok && mediaType === "image") {
      const downloadUrl = `${homeserverUrl}/_matrix/client/v1/media/download/${serverName}/${mediaId}`;
      res = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    }

    if (!res.ok) throw new Error(`Media fetch failed: ${res.status}`);

    const blob = await typedMediaBlob(await res.blob(), mediaType, mimeType);

    // Not awaited, and allowed to fail: a full Cache Storage (iOS keeps it
    // small) used to throw here, and the file the user just downloaded was
    // dropped with it — the voice message then never got a source at all.
    // The type travels along as Content-Type; a blob read back without one is
    // untyped, and WebKit refuses to play an untyped audio blob.
    cache?.put(cacheKey, new Response(blob.slice(), { headers: { "Content-Type": blob.type } })).catch(() => {});

    const blobUrl = URL.createObjectURL(blob);
    mediaCache.set(mxcUrl, blobUrl);
    return blobUrl;
  } catch (err) {
    console.warn("[BryghtChat] Failed to load media:", mxcUrl, err);
    return null;
  }
}

const GENERIC_TYPES = ["", "application/octet-stream", "binary/octet-stream"];

// A media blob WebKit can play: audio and video need a MIME type on a blob URL.
// Without one Safari/WKWebView cannot pick a media engine and fails with
// NotSupportedError (seen with the fragmented MP4 its own MediaRecorder writes).
// The cache entries written before this fix carry no type at all, and the
// homeserver may answer with a generic one — so the event's declared type wins,
// then a real type from the response, then the file's own signature. A generic
// declared type (a client that did not know better) counts as none: retyping
// the blob to application/octet-stream is the very failure this avoids.
async function typedMediaBlob(blob, mediaType, mimeType) {
  if (mediaType !== "audio" && mediaType !== "video") return blob;

  const declared = GENERIC_TYPES.includes(mimeType || "") ? null : mimeType;
  const type = declared || (GENERIC_TYPES.includes(blob.type) ? await sniffMediaType(blob, mediaType) : blob.type);
  if (!type || type === blob.type) return blob;
  return new Blob([blob], { type });
}

export async function sniffMediaType(blob, mediaType = "audio") {
  try {
    const bytes = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const ascii = (from, to) => String.fromCharCode(...bytes.slice(from, to));

    if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) return `${mediaType}/webm`;
    if (ascii(4, 8) === "ftyp") return `${mediaType}/mp4`;
    if (ascii(0, 4) === "OggS") return `${mediaType}/ogg`;
    if (mediaType === "audio" && bytes[0] === 0xff && (bytes[1] & 0xf6) === 0xf0) return "audio/aac";
    if (mediaType === "audio" && (ascii(0, 3) === "ID3" || (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0))) return "audio/mpeg";
    if (mediaType === "audio" && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WAVE") return "audio/wav";
  } catch (_) { /* unreadable: leave the type to the browser */ }
  return null;
}

// A short-lived error line above the composer, for anything that kept a message
// from going out.
export function showComposerError(composerEl, message) {
  const indicator = document.createElement("div");
  indicator.className = "bryght-upload-indicator bryght-upload-error";
  indicator.textContent = message;
  composerEl.insertAdjacentElement("beforebegin", indicator);
  setTimeout(() => indicator.remove(), 5000);
}

// `roomId` is the room's Matrix id, or a function resolving to it: a draft
// conversation has no room until its first message, so the composer hands over
// a resolver that creates it — asked only once the upload is through, so an
// upload that fails creates nothing.
export async function uploadAndSendFile(client, roomId, file, composerEl, caption = "", t = (k, f) => f) {
  const container = document.getElementById("bryght-chat");
  const { homeserverUrl, accessToken } = container.dataset;

  // Checked here rather than in each caller: this is the one point all three
  // entry paths pass through, and it sees the file *after* HEIC conversion.
  if (file.size > MAX_UPLOAD_BYTES) {
    const limitMb = Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024));
    showComposerError(
      composerEl,
      t("upload_too_large", `${file.name} ist zu groß (max. ${limitMb} MB)`)
        .replace("%{filename}", file.name)
        .replace("%{limit}", limitMb),
    );
    return;
  }

  const indicator = document.createElement("div");
  indicator.className = "bryght-upload-indicator";
  indicator.textContent = t("uploading", `Hochladen: ${file.name}…`).replace("%{filename}", file.name);
  composerEl.insertAdjacentElement("beforebegin", indicator);

  try {
    // Note: HEIC conversion is now handled in attachment_preview.js
    // before this function is called, so the file is already converted.

    const encodedFilename = encodeURIComponent(file.name);
    const uploadUrl = `${homeserverUrl}/_matrix/media/v3/upload?filename=${encodedFilename}`;

    const uploadRes = await fetch(uploadUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!uploadRes.ok) {
      throw new Error(`Upload failed: ${uploadRes.status}`);
    }

    const { content_uri: mxcUrl } = await uploadRes.json();
    const localBlobUrl = URL.createObjectURL(file);
    mediaCache.set(mxcUrl, localBlobUrl);
    // What we just uploaded is the original, so the lightbox never has to
    // fetch back the file this device already holds.
    fullMediaCache.set(mxcUrl, localBlobUrl);

    let msgtype = "m.file";
    if (file.type.startsWith("image/")) msgtype = "m.image";
    else if (file.type.startsWith("video/")) msgtype = "m.video";
    else if (file.type.startsWith("audio/")) msgtype = "m.audio";

    const content = {
      body: caption || file.name,
      filename: file.name,
      msgtype,
      url: mxcUrl,
      info: {
        mimetype: file.type,
        size: file.size,
      },
    };

    if (msgtype === "m.image") {
      try {
        const dims = await getImageDimensions(file);
        content.info.w = dims.width;
        content.info.h = dims.height;
      } catch (_) { }
    }

    const targetRoomId = typeof roomId === "function" ? await roomId() : roomId;
    await client.sendEvent(targetRoomId, "m.room.message", content);
    console.log("[BryghtChat] File sent:", file.name, msgtype, caption ? `caption: "${caption}"` : "");
  } catch (err) {
    console.error("[BryghtChat] File upload failed:", err);
    indicator.textContent = err.userMessage
      || t("upload_error", `Fehler: ${file.name} konnte nicht hochgeladen werden`).replace("%{filename}", file.name);
    indicator.classList.add("bryght-upload-error");
    setTimeout(() => indicator.remove(), 3000);
    return;
  }

  indicator.remove();
}

export function downloadAuthenticatedFile(mxcUrl, filename) {
  if (!mxcUrl || !mxcUrl.startsWith("mxc://")) return;

  const parts = mxcUrl.replace("mxc://", "").split("/");
  if (parts.length < 2) return;

  const serverName = parts[0];
  const mediaId = parts.slice(1).join("/");

  const container = document.getElementById("bryght-chat");
  const { homeserverUrl, accessToken } = container.dataset;
  const url = `${homeserverUrl}/_matrix/client/v1/media/download/${serverName}/${mediaId}`;

  fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
    .then((res) => {
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch((err) => {
      console.error("[BryghtChat] Download failed:", err);
    });
}

export async function loadFullMedia(mxcUrl, mimeType = null) {
  if (!mxcUrl || !mxcUrl.startsWith("mxc://")) return null;
  if (fullMediaCache.has(mxcUrl)) return fullMediaCache.get(mxcUrl);

  // GIFs are never thumbnailed, so whatever the timeline loaded is the original.
  if (mimeType === "image/gif" && mediaCache.has(mxcUrl)) return mediaCache.get(mxcUrl);

  const parts = mxcUrl.replace("mxc://", "").split("/");
  if (parts.length < 2) return null;

  const serverName = parts[0];
  const mediaId = parts.slice(1).join("/");
  const cacheKey = `${mxcUrl.replace("mxc://", "https://bryght-media-cache/")}?full=1`;

  try {
    // Optional on purpose: without Cache Storage (private modes, insecure
    // contexts) the original still loads, it just isn't kept between reloads.
    const cache = typeof caches === "undefined" ? null : await caches.open(MEDIA_CACHE_NAME).catch(() => null);

    const cachedResponse = await cache?.match(cacheKey);
    if (cachedResponse) {
      const blobUrl = URL.createObjectURL(await cachedResponse.blob());
      fullMediaCache.set(mxcUrl, blobUrl);
      return blobUrl;
    }

    const container = document.getElementById("bryght-chat");
    const { homeserverUrl, accessToken } = container.dataset;
    const res = await fetch(
      `${homeserverUrl}/_matrix/client/v1/media/download/${serverName}/${mediaId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    if (!res.ok) throw new Error(`Media fetch failed: ${res.status}`);

    const blob = await res.blob();
    await cache?.put(cacheKey, new Response(blob.slice())).catch(() => {});

    const blobUrl = URL.createObjectURL(blob);
    fullMediaCache.set(mxcUrl, blobUrl);
    return blobUrl;
  } catch (err) {
    console.warn("[BryghtChat] Failed to load full media:", mxcUrl, err);
    return null;
  }
}
